import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useTranslation } from "react-i18next";
import { semanticColors } from "../../../utils/colors";
import { typography } from "../../../utils/typography";

const slotSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 1,
  minHeight: "180px",
  p: 2,
  borderRadius: "8px",
  border: `1px dashed ${semanticColors.border.middle}`,
  backgroundColor: semanticColors.surface.canvas,
  cursor: "pointer",
  textAlign: "center" as const
};

const activeSx = {
  borderColor: semanticColors.border.focus,
  backgroundColor: semanticColors.surface.containerLow
};

const captionSx = {
  ...typography.body.m,
  color: semanticColors.text.secondary
};

const hintSx = {
  ...typography.body.s,
  color: semanticColors.text.secondary
};

const errorSx = {
  ...typography.body.s,
  color: semanticColors.status.error,
  mt: 0.5
};

const uploadIconSx = { color: semanticColors.text.secondary };

/** The side's name, centred under the slot it names. */
const sideLabelSx = {
  ...captionSx,
  mt: 0.5,
  textAlign: "center" as const
};

const previewSx = {
  maxWidth: "100%",
  maxHeight: "220px",
  borderRadius: "8px",
  display: "block"
};

/** The scans are photographed, so only images are useful here. */
const ACCEPTED_TYPES = "image/*";

/**
 * One side of a document, chosen by dropping a file or clicking to browse.
 *
 * The file is held here, not uploaded: the caller decides when it is sent, so
 * that a half-filled form never leaves a stray image in storage. What is shown
 * is a preview of the chosen file, falling back to the image already on record
 * while nothing new has been picked.
 */
export default function ImageDropField({
  label,
  currentUrl,
  file,
  onSelect
}: {
  label: string;
  /** The side already on record, shown until a new file is chosen. */
  currentUrl?: string;
  file: File | null;
  onSelect: (file: File) => void;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * Counts nested enter/leave pairs rather than holding a boolean: dragging
   * over a child fires `dragleave` on the parent, which would drop the
   * highlight while the pointer is still inside the slot.
   */
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    // Object URLs are held by the document until revoked, so each one is
    // released when it is replaced or the field goes away.
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const accept = useCallback(
    (candidate: File | undefined) => {
      if (!candidate) return;
      if (!candidate.type.startsWith("image/")) {
        setError(t("identifyingDocuments.imageOnly"));
        return;
      }
      setError(null);
      onSelect(candidate);
    },
    [onSelect, t]
  );

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      accept(event.dataTransfer.files?.[0]);
    },
    [accept]
  );

  const shown = preview ?? currentUrl;
  /** Merged here rather than in the markup, so the slot keeps one style prop. */
  const slotStyle = dragging ? { ...slotSx, ...activeSx } : slotSx;

  return (
    <Box>
      <Box
        sx={slotStyle}
        onClick={() => inputRef.current?.click()}
        onDragEnter={event => {
          event.preventDefault();
          dragDepth.current += 1;
          setDragging(true);
        }}
        onDragLeave={() => {
          dragDepth.current -= 1;
          if (dragDepth.current <= 0) setDragging(false);
        }}
        onDragOver={event => event.preventDefault()}
        onDrop={onDrop}
      >
        {shown ? (
          <Box component="img" src={shown} alt={label} sx={previewSx} />
        ) : (
          <>
            <UploadFileIcon sx={uploadIconSx} />
            <Typography sx={hintSx}>
              {t("identifyingDocuments.dropHint")}
            </Typography>
          </>
        )}
      </Box>
      <Typography sx={sideLabelSx}>{label}</Typography>
      {error && <Typography sx={errorSx}>{error}</Typography>}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        hidden
        onChange={event => {
          accept(event.target.files?.[0]);
          // Cleared so choosing the same file twice still fires a change.
          event.target.value = "";
        }}
      />
    </Box>
  );
}
